import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Skeleton,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Psychology,
  Work,
  AccountCircle,
  Logout,
  Shield,
  Sync,
  Settings,
  CloudUpload,
  Groups,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import PIJobSyncDialog from '../PIJobSyncDialog';
import { PIJobSyncResults } from '../../services/api';

const drawerWidth = 240;

interface NavigationItem {
  text: string;
  icon: React.ReactElement;
  path?: string;
  action?: 'import'; // Special action instead of navigation
  roles?: string[];
  testId: string;
}

const navigationItems: NavigationItem[] = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard', testId: 'nav-dashboard' },
  { text: 'Jobs', icon: <Work />, path: '/jobs', testId: 'nav-jobs' },
  { text: 'All Candidates', icon: <People />, path: '/candidates', testId: 'nav-candidates' },
  { text: 'Teams', icon: <Groups />, path: '/teams', testId: 'nav-teams', roles: ['admin', 'hr', 'manager'] },
  { text: 'Import from PI', icon: <Sync />, action: 'import', testId: 'nav-import', roles: ['admin', 'hr', 'manager'] },
  { text: 'Upload Documents', icon: <CloudUpload />, path: '/candidates/import', testId: 'nav-upload-docs', roles: ['admin', 'hr', 'manager'] },
  { text: 'PI Profiles', icon: <Psychology />, path: '/pi-profiles', testId: 'nav-pi-profiles' },
  { text: 'Admin', icon: <Shield />, path: '/admin', testId: 'nav-admin', roles: ['admin'] },
  { text: 'Settings', icon: <Settings />, path: '/settings', testId: 'nav-settings', roles: ['admin'] },
];

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const { user, logout, loading } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleMenuClose();
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleItemClick = (item: NavigationItem) => {
    if (item.action === 'import') {
      setImportDialogOpen(true);
      setMobileOpen(false);
    } else if (item.path) {
      handleNavigation(item.path);
    }
  };

  const handleImportComplete = (results: PIJobSyncResults) => {
    const { jobs, candidates } = results;
    const parts = [];
    if (jobs.created > 0) parts.push(`${jobs.created} jobs created`);
    if (jobs.linked > 0) parts.push(`${jobs.linked} jobs linked`);
    if (candidates.imported > 0) parts.push(`${candidates.imported} candidates imported`);

    if (parts.length > 0) {
      showNotification(`Import complete: ${parts.join(', ')}`, 'success');
    } else {
      showNotification('No changes were made', 'info');
    }
  };

  const isItemAllowed = (item: NavigationItem) => {
    if (!item.roles) return true;
    return user?.role && item.roles.includes(user.role);
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          PI Evaluator
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {loading ? (
          // Show skeleton loaders while auth is loading
          Array.from({ length: 4 }).map((_, index) => (
            <ListItem key={`skeleton-${index}`} disablePadding>
              <ListItemButton disabled>
                <ListItemIcon>
                  <Skeleton variant="circular" width={24} height={24} />
                </ListItemIcon>
                <ListItemText>
                  <Skeleton variant="text" width={100} />
                </ListItemText>
              </ListItemButton>
            </ListItem>
          ))
        ) : (
          // Show actual navigation items once loaded
          navigationItems.filter(isItemAllowed).map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={item.path ? location.pathname === item.path : false}
                onClick={() => handleItemClick(item)}
                data-testid={item.testId}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))
        )}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Wagner Meters - Hiring Tool
          </Typography>
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenuOpen}
            color="inherit"
            disabled={loading}
          >
            {loading ? (
              <Skeleton variant="circular" width={32} height={32} />
            ) : (
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </Avatar>
            )}
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <Typography variant="inherit">
                {user?.first_name} {user?.last_name} ({user?.role})
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => {
              handleMenuClose();
              navigate('/profile');
            }}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              My Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>

      {/* PI Import Dialog */}
      <PIJobSyncDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onComplete={handleImportComplete}
      />
    </Box>
  );
};

export default Layout;